import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import ResultTemplateCodeEditor from './ResultTemplateCodeEditor.jsx';
import {
    SAMPLE_LONGEST_CELL,
    SAMPLE_RANDOM,
    fetchResultTemplateDefaults,
    previewResultTemplate,
} from '../../../common/resultTemplateApi.js';

// The kiosk's own viewport, per the meta tag in result.html. The preview is rendered at
// exactly this size because a screen that fits at some other size tells you nothing.
const KIOSK_WIDTH = 800;
const KIOSK_HEIGHT = 480;

const findingKey = (finding, index) => `${finding.code || 'finding'}-${finding.line || index}`;

const errorLinesOf = (findings = []) =>
    findings.map((finding) => finding.line).filter((line) => Number.isInteger(line));

export default function ResultTemplateDialog({
    open,
    onClose,
    wasteReceiverId,
    html,
    css,
    onChange,
    saveFindings = null,
    disabled = false,
}) {
    const [tab, setTab] = useState('html');
    const [previewHtml, setPreviewHtml] = useState('');
    const [selection, setSelection] = useState(null);
    const [findings, setFindings] = useState({ errors: [], warnings: [] });
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    // The station's built-in files, shown when this receiver has no template of its own.
    // Held here rather than pushed into the draft: a receiver that follows the station
    // default must keep following it, and it would stop the moment the draft carried a copy.
    const [stationDefault, setStationDefault] = useState({ html: '', css: '' });
    // The longest-cell row is pinned once found: comparing two CSS revisions against two
    // different rows is not a comparison. "Find længste igen" clears it.
    const [pinnedRowIndex, setPinnedRowIndex] = useState(null);
    const [seeded, setSeeded] = useState(false);

    // Rapid clicks on "Tilfældig række" would otherwise race, and the slower response could
    // land last and overwrite the newer one.
    const requestIdRef = useRef(0);

    const isCustom = Boolean(html.trim() || css.trim());
    // What the editors show and what the preview renders: this receiver's own template, or
    // the station's screen when it has none.
    const shownHtml = isCustom ? html : stationDefault.html;
    const shownCss = isCustom ? css : stationDefault.css;

    const loadStationDefault = useCallback(async () => {
        setBusy(true);
        setMessage('');
        try {
            const defaults = await fetchResultTemplateDefaults();
            setStationDefault({ html: defaults.html, css: defaults.css });
        } catch (error) {
            setMessage(error.message);
        } finally {
            setBusy(false);
        }
    }, []);

    // Editing is what creates the override. Until then the receiver carries no
    // result_template and keeps following whatever the station ships.
    const editTemplate = useCallback(
        (next) => onChange({ html: shownHtml, css: shownCss, ...next }),
        [onChange, shownHtml, shownCss],
    );

    // Reset means "go back to following the station", so it removes the override rather
    // than storing a copy of today's default.
    const resetToStationDefault = useCallback(() => {
        onChange({ html: '', css: '' });
        loadStationDefault();
    }, [onChange, loadStationDefault]);

    // A receiver with no template of its own runs the station's built-in screen, so the
    // editor opens on those exact bytes -- editing from a blank box would make the first
    // save a rewrite rather than a change. Fetched on open regardless, since "reset to the
    // station default" needs them too.
    useEffect(() => {
        if (!open || seeded) return;
        setSeeded(true);
        loadStationDefault();
    }, [open, seeded, loadStationDefault]);

    // A save blocked by the gate opens this dialog with its report, so the offending line
    // is marked rather than described in a snackbar the operator has to translate.
    useEffect(() => {
        if (!saveFindings) return;
        setFindings({
            errors: saveFindings.errors || [],
            warnings: saveFindings.warnings || [],
        });
    }, [saveFindings]);

    useEffect(() => {
        if (open) return;
        setSeeded(false);
        setPreviewHtml('');
        setSelection(null);
        setFindings({ errors: [], warnings: [] });
        setMessage('');
    }, [open]);

    const runPreview = useCallback(
        async ({ sample, rowIndex }) => {
            if (!wasteReceiverId) {
                setMessage('Gem affaldsmodtageren én gang, før du kan se en visning.');
                return;
            }

            const requestId = requestIdRef.current + 1;
            requestIdRef.current = requestId;
            setBusy(true);
            setMessage('');

            try {
                const result = await previewResultTemplate({
                    wasteReceiverId,
                    html: shownHtml,
                    css: shownCss,
                    sample,
                    ...(Number.isInteger(rowIndex) ? { rowIndex } : {}),
                });

                if (requestIdRef.current !== requestId) return;

                setPreviewHtml(result.rendered_html);
                setSelection(result.selection);
                setPinnedRowIndex(result.selection?.row_index ?? null);
                setFindings({ errors: [], warnings: result.validation?.warnings || [] });
            } catch (error) {
                if (requestIdRef.current !== requestId) return;

                setFindings({ errors: error.errors || [], warnings: error.warnings || [] });
                setMessage(error.errors?.length ? '' : error.message);
            } finally {
                if (requestIdRef.current === requestId) setBusy(false);
            }
        },
        [wasteReceiverId, shownHtml, shownCss],
    );

    const activeErrorLines = errorLinesOf(findings.errors);

    const renderSelectionBadge = () => {
        if (!selection) return null;

        const label = selection.column_display_name
            ? `Række ${selection.row_index} · ${selection.column_display_name} (${selection.value_length} tegn)`
            : `Række ${selection.row_index} · alle celler tomme`;

        return <Chip size="small" label={label} className="setup-chip" />;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className="setup-dialog result-template-dialog"
            fullWidth
            maxWidth="xl"
        >
            <DialogTitle>Resultatskærm</DialogTitle>
            <DialogContent className="result-template-dialog-content">
                <div className="result-template-layout">
                    <div className="result-template-editor-pane">
                        <Tabs value={tab} onChange={(event, next) => setTab(next)}>
                            <Tab value="html" label="result.html" />
                            <Tab value="css" label="result.css" />
                        </Tabs>

                        <div className="result-template-editor" hidden={tab !== 'html'}>
                            <ResultTemplateCodeEditor
                                language="html"
                                value={shownHtml}
                                onChange={(next) => editTemplate({ html: next })}
                                errorLines={activeErrorLines}
                                readOnly={disabled}
                            />
                        </div>
                        <div className="result-template-editor" hidden={tab !== 'css'}>
                            <ResultTemplateCodeEditor
                                language="css"
                                value={shownCss}
                                onChange={(next) => editTemplate({ css: next })}
                                readOnly={disabled}
                            />
                        </div>

                        <p className="result-template-help">
                            {isCustom
                                ? 'Tilpasset skabelon. Den gemmes sammen med resten af affaldsmodtageren, når du trykker Gem. Vær forsigtig med dine ændringer og test altid på ægte hardware. Ændringer træder i kraft næste gang programmet genstartes.'
                                : 'Viser standard resultatskærmen for dangerous wade. Så snart du retter i den, oprettes en tilpasning for denne affaldsmodtager — indtil da følger den standard versionen.'}
                        </p>
                    </div>

                    <div className="result-template-preview-pane">
                        <div className="result-template-preview-actions">
                            <Button
                                variant="contained"
                                size="small"
                                disabled={busy || disabled}
                                onClick={() => {
                                    setPinnedRowIndex(null);
                                    runPreview({ sample: SAMPLE_LONGEST_CELL });
                                }}
                            >
                                {pinnedRowIndex === null ? 'Længste tekst' : 'Find længste igen'}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={busy || disabled}
                                onClick={() => {
                                    setPinnedRowIndex(null);
                                    runPreview({ sample: SAMPLE_RANDOM });
                                }}
                            >
                                Tilfældig række
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<RefreshIcon />}
                                disabled={busy || disabled || pinnedRowIndex === null}
                                onClick={() =>
                                    runPreview({
                                        sample: SAMPLE_LONGEST_CELL,
                                        rowIndex: pinnedRowIndex,
                                    })
                                }
                            >
                                Opdater visning
                            </Button>
                            {busy && <CircularProgress size={18} />}
                        </div>

                        <div className="result-template-preview-meta">
                            {renderSelectionBadge()}
                        </div>

                        <iframe
                            title="Resultatskærm"
                            className="result-template-preview-frame"
                            width={KIOSK_WIDTH}
                            height={KIOSK_HEIGHT}
                            // allow-scripts without allow-same-origin: the template's own
                            // script runs, so a JS error in it is visible here rather than
                            // in the field, but it reaches nothing of this app.
                            sandbox="allow-scripts"
                            srcDoc={previewHtml}
                        />

                        {message && <Alert severity="warning">{message}</Alert>}

                        {findings.errors.length > 0 && (
                            <Alert severity="error">
                                <AlertTitle>Skabelonen kan ikke gemmes</AlertTitle>
                                <ul className="result-template-finding-list">
                                    {findings.errors.map((finding, index) => (
                                        <li key={findingKey(finding, index)}>
                                            {finding.line ? `Linje ${finding.line}: ` : ''}
                                            {finding.message}
                                        </li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        {findings.warnings.length > 0 && (
                            <Alert severity="warning">
                                <AlertTitle>Advarsler</AlertTitle>
                                <ul className="result-template-finding-list">
                                    {findings.warnings.map((finding, index) => (
                                        <li key={findingKey(finding, index)}>
                                            {finding.line ? `Linje ${finding.line}: ` : ''}
                                            {finding.message}
                                        </li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    startIcon={<RestartAltIcon />}
                    onClick={resetToStationDefault}
                    disabled={busy || disabled || !isCustom}
                >
                    Nulstil til stationens standard
                </Button>
                <Button variant="contained" onClick={onClose}>
                    Luk
                </Button>
            </DialogActions>
        </Dialog>
    );
}
